import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import isArray from 'lodash/isArray';
import { EVENT_INFO } from './match-list.constants';
import {
    AggregatorOneOfFilteredTypeModel,
    ExtendedFilteredMarkets,
    FilteredBettingOffers,
    FilteredEventInfos,
    FilteredMainMarkets,
    FilteredMarkets,
    FilteredMarketsAndOutcomeRelation,
    FilteredMatches,
    FilteredMatchesWithEvents,
    FilteredOutcomes,
    FilteredStreamingProvider,
    FilterItemsFromRecordsReturn,
    FilteredEventStatistics,
    FilteredMarketGroupsOverview,
    MarketGroupsOverviewWithMarket,
} from './match-list-models';
import { sortByMarketType, sortByParamFloat1 } from './match-list.utils';
import { AggregatorDisciplineModel, AggregatorOneOfTypeModel, ModelTypes } from '@om-fe/models';
import getConfig from '@om-fe/core/utils/config';

export enum FilterAndPickReturnType {
    ArrayOfObjects = 'ArrayOfObjects',
    ArrayOfIds = 'ArrayOfIds',
}

interface FilterAndPickOptions {
    readonly filterPredicate: [string, ModelTypes] | Record<string, ModelTypes | boolean>;
    readonly data: AggregatorOneOfTypeModel[];
    readonly groupDataBy?: string;
    readonly returnType?: string;
    readonly filterBySportId?: string;
    readonly filerByEventId?: string[];
    readonly filerByOutcomeId?: string[];
    readonly filterByMainMarket?: boolean;
    readonly filterByAllowLiveOdds?: boolean;
    readonly special?: boolean;
}

const typesWithSportId: string[] = [ModelTypes.MATCH, ModelTypes.MAIN_MARKET];

function getEventIdList(matches: FilteredMatches[]): string[] {
    return matches.map((match: FilteredMatches): string => {
        return match.id;
    });
}

/**
 * Used to filter all event info types from the records
 */
export function filterAndPick({ filterPredicate, data, groupDataBy, returnType, filterBySportId, filerByEventId, filerByOutcomeId, filterByMainMarket, filterByAllowLiveOdds, special }: FilterAndPickOptions): AggregatorOneOfFilteredTypeModel[]
    | Record<string, AggregatorOneOfFilteredTypeModel[]>
    | Record<string, AggregatorOneOfFilteredTypeModel>
    | string[] {

    const filteredData: AggregatorOneOfTypeModel[] = filter(data, filterPredicate);
    const valuesToReturn: AggregatorOneOfFilteredTypeModel[] =
        filteredData.map((el: AggregatorOneOfTypeModel): AggregatorOneOfFilteredTypeModel => remapEntries(el));

    if (returnType === FilterAndPickReturnType.ArrayOfObjects) {
        if (filerByEventId) {
            return valuesToReturn.filter((elem: FilteredMarkets) => filerByEventId.includes(elem.eventId));
        }

        if (filterBySportId) {
            return valuesToReturn.filter((elem: FilteredMainMarkets) => elem.sportId === filterBySportId);
        }

        if (filterByMainMarket) {
            return valuesToReturn.filter((elem: FilteredMainMarkets, index: number) => {
                return valuesToReturn.findIndex((item: FilteredMainMarkets) => elem.bettingTypeId === item.bettingTypeId && elem.eventPartId === item.eventPartId) === index
            });
        }

        if (filterByAllowLiveOdds) {
            return valuesToReturn.filter((elem: FilteredMatchesWithEvents) => elem.allowsLiveOdds === filterByAllowLiveOdds);
        }

        return valuesToReturn;
    }

    if (returnType === FilterAndPickReturnType.ArrayOfIds) {
        if (filterBySportId) {
            return valuesToReturn.filter((elem: FilteredMainMarkets) => elem.sportId === filterBySportId)
                .map((item: AggregatorOneOfFilteredTypeModel): string  => item!.id);
        }

        if (filterByMainMarket) {
            if (special) {
                return valuesToReturn.map((item: AggregatorOneOfFilteredTypeModel): string  => item!.id);
            } else {
                return valuesToReturn.filter((elem: FilteredMainMarkets, index: number) => {
                    return valuesToReturn.findIndex((item: FilteredMainMarkets) => elem.bettingTypeId === item.bettingTypeId && elem.eventPartId === item.eventPartId) === index
                }).map((item: AggregatorOneOfFilteredTypeModel): string  => item!.id);
            }
        }

        return valuesToReturn
            .map((item: AggregatorOneOfFilteredTypeModel): string  => item!.id);
    }

    if (groupDataBy) {
        return groupBy(valuesToReturn, groupDataBy);
    }

    if (filterBySportId && isArray(filterPredicate) && (filterPredicate.includes(typesWithSportId[0]) || filterPredicate.includes(typesWithSportId[1]))) {
        const filteredArrayBySport: AggregatorOneOfFilteredTypeModel[] = [];

        valuesToReturn.forEach((item: FilteredMainMarkets | FilteredMatches, index: number) => {
            if (item.sportId === filterBySportId) {
                filteredArrayBySport.push(valuesToReturn[index]);
            }
        });

        return keyBy(filteredArrayBySport, 'id');
    }

    if (filerByEventId) {
        const filteredArrayByEventId: AggregatorOneOfFilteredTypeModel[] = [];

        valuesToReturn.forEach((item: FilteredEventInfos | FilteredOutcomes, index: number) => {
            if (filerByEventId.includes(item.eventId)) {
                filteredArrayByEventId.push(valuesToReturn[index]);
            }
        });

        return keyBy(filteredArrayByEventId, 'id');
    }

    if (filerByOutcomeId) {
        const filteredArrayByOutcomeId: AggregatorOneOfFilteredTypeModel[] = [];

        valuesToReturn.forEach((item: FilteredBettingOffers, index: number) => {
            if (filerByOutcomeId.includes(item.outcomeId)) {
                filteredArrayByOutcomeId.push(valuesToReturn[index]);
            }
        });

        return keyBy(filteredArrayByOutcomeId, groupDataBy || 'id');
    }

    if (filterByMainMarket) {
        let filteredArrayByMainMarket: AggregatorOneOfFilteredTypeModel[] = [];
        if (special) {
            filteredArrayByMainMarket = valuesToReturn;
        } else {
            filteredArrayByMainMarket = valuesToReturn.filter((elem: FilteredMainMarkets, index: number) => {
                return valuesToReturn.findIndex((item: FilteredMainMarkets) => elem.bettingTypeId === item.bettingTypeId && elem.eventPartId === item.eventPartId) === index
            });
        }
        
        return keyBy(filteredArrayByMainMarket, 'id');
    }

    return keyBy(valuesToReturn, 'id');
}

export function remapEntries(element: AggregatorOneOfTypeModel): AggregatorOneOfFilteredTypeModel {
    switch (element._type) {
        case ModelTypes.SPORT:
            return {
                id: element.id,
                isVirtual: element.isVirtual,
                name: element.name,
                parentId: element.parentId,
                parentName: element.parentName,
                numberOfBettingOffers: element.numberOfBettingOffers,
                numberOfEvents: element.numberOfEvents,
                numberOfLiveBettingOffers: element.numberOfLiveBettingOffers,
                numberOfLiveEvents: element.numberOfLiveEvents,
                numberOfUpcomingMatches: element.numberOfUpcomingMatches,
                numberOfLiveMarkets: element.numberOfLiveMarkets,
                numberOfMarkets: element.numberOfMarkets,
                shortName: element.shortName,
                showEventCategory: element.showEventCategory,
                displayChildren: element.displayChildren,
                childrenIds: element.childrenIds,
            };
        case ModelTypes.MATCH:
            return {
                _type: element._type,
                id: element.id,
                categoryId: element.categoryId,
                categoryName: element.categoryName,
                awayParticipantName: element.awayParticipantName,
                awayParticipantId: element.awayParticipantId,
                homeParticipantName: element.homeParticipantName,
                homeParticipantId: element.homeParticipantId,
                awayShortParticipantName: element.awayShortParticipantName,
                homeShortParticipantName: element.homeShortParticipantName,
                homeParticipantLogoUrl: element.homeParticipantLogoUrl,
                awayParticipantLogoUrl: element.awayParticipantLogoUrl,
                name: element.name,
                sportName: element.sportName,
                statusId: element.statusId,
                shortSportName: element.shortSportName,
                shortVenueName: element.shortVenueName,
                numberOfMarkets: element.numberOfMarkets,
                venueName: element.venueName,
                parentName: element.parentName,
                parentId: element.parentId,
                sportId: element.sportId,
                shortParentName: element.shortParentName,
                startTime: element.startTime,
                venueId: element.venueId,
                isAvailableForSimulation: element.isAvailableForSimulation,
            };
        case ModelTypes.TOURNAMENT:
            return {
                _type: element._type,
                id: element.id,
                categoryId: element.categoryId,
                categoryName: element.categoryName,
                homeParticipantName: element.translatedName,
                homeShortParticipantName: element.shortTranslatedName,
                name: element.name,
                sportName: element.sportName,
                statusId: element.statusId,
                shortSportName: element.shortSportName,
                shortVenueName: element.shortVenueName,
                numberOfMarkets: element.numberOfMarkets,
                venueName: element.venueName,
                parentName: element.translatedName,
                parentId: element.id,
                sportId: element.sportId,
                shortParentName: element.shortTranslatedName,
                startTime: element.startTime,
                venueId: element.venueId,
                allowsLiveOdds: element.allowsLiveOdds,
            };
        case ModelTypes.EVENT_INFO:
            return {
                eventPartId: element.eventPartId,
                eventId: element.eventId,
                typeId: element.typeId,
                typeName: element.typeName,
                statusId: element.statusId,
                paramFloat1: element.paramFloat1,
                paramFloat2: element.paramFloat2,
                paramParticipantId1: element.paramParticipantId1,
                paramParticipantId2: element.paramParticipantId2,
                eventPartRelativePosition: element.eventPartRelativePosition,
                paramEventPartName1: element.paramEventPartName1,
                shortParamEventPartName1: element.shortParamEventPartName1,
                paramEventPartId1: element.paramEventPartId1,
                id: element.id,
                eventPartName: element.eventPartName,
            };
        case ModelTypes.MAIN_MARKET:
            return {
                bettingTypeId: element.bettingTypeId,
                bettingTypeName: element.bettingTypeName,
                eventPartId: element.eventPartId,
                eventPartName: element.eventPartName,
                id: element.id,
                numberOfOutcomes: element.numberOfOutcomes,
                sportId: element.sportId,
                hasSelector: element.hasSelector,
                outright: element.outright,
            };
        case ModelTypes.MARKET:
            return {
                bettingTypeId: element.bettingTypeId,
                bettingTypeName: element.bettingTypeName,
                eventId: element.eventId,
                eventPartId: element.eventPartId,
                eventPartName: element.eventPartName,
                id: element.id,
                idAsString: element.idAsString,
                isClosed: element.isClosed,
                isComplete: element.isComplete,
                name: element.name,
                numberOfOutcomes: element.numberOfOutcomes,
                paramFloat1: element.paramFloat1,
                paramParticipantId1: element.paramParticipantId1,
                scoringUnitId: element.scoringUnitId,
                scoringUnitName: element.scoringUnitName,
                shortBettingTypeName: element.shortBettingTypeName,
                shortEventPartName: element.shortEventPartName,
                minSelections: element.minSelections,
                groupIds: element.groupIds,
                labelName: element.labelName,
                labelStyle: element.labelStyle,
                isAvailableForSimulation: element.isAvailableForSimulation,
            };
        case ModelTypes.BETTING_OFFER:
            return {
                bettingTypeId: element.bettingTypeId,
                bettingTypeName: element.bettingTypeName,
                id: element.id,
                idAsString: element.idAsString,
                isAvailable: element.isAvailable,
                isLive: element.isLive,
                lastChangedTime: element.lastChangedTime,
                marketName: element.marketName,
                odds: element.odds,
                outcomeId: element.outcomeId,
                providerId: element.providerId,
                shortBettingTypeName: element.shortBettingTypeName,
                statusId: element.statusId,
            };
        case ModelTypes.OUTCOME:
            return {
                code: element.code,
                eventId: element.eventId,
                eventPartId: element.eventPartId,
                eventPartName: element.eventPartName,
                headerName: element.headerName,
                headerNameKey: element.headerNameKey,
                headerShortName: element.headerShortName,
                id: element.id,
                idAsString: element.idAsString,
                paramFloat1: element.paramFloat1,
                paramParticipantId1: element.paramParticipantId1,
                paramParticipantName1: element.paramParticipantName1,
                paramScoringUnitId1: element.paramScoringUnitId1,
                paramScoringUnitName1: element.paramScoringUnitName1,
                shortEventPartName: element.shortEventPartName,
                shortParamParticipantName1: element.shortParamParticipantName1,
                shortTranslatedName: element.shortTranslatedName,
                statusId: element.statusId,
                translatedName: element.translatedName,
                typeId: element.typeId,
                typeName: element.typeName,
            };
        case ModelTypes.MARKET_OUTCOME_RELATION:
            return {
                id: element.id,
                idAsString: element.idAsString,
                marketId: element.marketId,
                outcomeId: element.outcomeId,
            };
        case ModelTypes.STREAMING_PROVIDER_EVENT_RELATION:
            return {
                eventId: element.eventId,
                id: element.id,
                name: element.name,
                streamingProviderId: element.streamingProviderId,
                url: element.url,
                _type: element._type,
            };
        case ModelTypes.EVENT_STATISTICS:
            return {
                id: element.id,
                entityId: element.entityId,
                providerEntityId: element.providerEntityId,
                providerId: element.providerId,
                _type: element._type,
                providerHomeParticipantId: element.providerHomeParticipantId,
                providerParentEntityId: element.providerParentEntityId,
                providerAwayParticipantId: element.providerAwayParticipantId,
                sportId: element.sportId,
            };
        case ModelTypes.MARKET_GROUP_OVERVIEW:
            return {
                id: element.id,
                groupKey: element.groupKey,
                translatedName: element.translatedName,
                position: element.position,
                sportId: element.sportId,
                _type: element._type,
            };
        default:
            return null;
    }
}

export function filterItemsFromRecordsBySport(records: AggregatorOneOfTypeModel[], sportId: string): FilterItemsFromRecordsReturn {
    const filteredMatches = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MATCH || ModelTypes.TOURNAMENT],
        filterBySportId: sportId,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const eventIdList: string[] = getEventIdList(filteredMatches);

    const filteredMainMarkets = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MAIN_MARKET],
        filterBySportId: sportId,
        data: records,
    }) as Record<string, FilteredMainMarkets>;

    const filteredMainMarketsList = filterAndPick({
        filterPredicate: {'_type': ModelTypes.MAIN_MARKET, 'outright': false},
        filterBySportId: sportId,
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    const filteredMainOutrightsMarketsList = filterAndPick({
        filterPredicate: {'_type': ModelTypes.MAIN_MARKET, 'outright': true},
        filterBySportId: sportId,
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    const filteredMarketGroupsOverviewList = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
        filterBySportId: sportId,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    const filteredMarketGroupsOverview = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
        filterBySportId: sportId,
    }) as Record<string, FilteredMarketGroupsOverview>;

    const filteredEventInfos = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_INFO],
        data: records,
        filerByEventId: eventIdList,
    }) as Record<string, FilteredEventInfos>;

    const filteredOutcomes = filterAndPick({
        filterPredicate: ['_type', ModelTypes.OUTCOME],
        data: records,
        filerByEventId: eventIdList,
    }) as Record<string, FilteredOutcomes>;

    const filteredMarkets = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET],
        data: records,
        filerByEventId: eventIdList,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
    }) as FilteredMarkets[];

    const filteredBettingOffers = filterAndPick({
        filterPredicate: ['_type', ModelTypes.BETTING_OFFER],
        data: records,
        filerByOutcomeId: Object.keys(filteredOutcomes),
    }) as Record<string, FilteredBettingOffers>;

    const filteredMarketAndOutcomeRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
        data: records,
        filerByOutcomeId: Object.keys(filteredOutcomes),
    }) as Record<string, FilteredMarketsAndOutcomeRelation>;

    const filteredStreamingProviderEventRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.STREAMING_PROVIDER_EVENT_RELATION],
        data: records,
        filerByEventId: eventIdList,
    }) as Record<string, FilteredStreamingProvider>;

    const filteredEventStatistics = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_STATISTICS],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredEventStatistics[];

    const mutableFilteredMarketAndOutcomeRelationOutcomeId =
        filterAndPick({
            filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
            data: records,
            groupDataBy: 'marketId',
            filerByOutcomeId: Object.keys(filteredOutcomes),
        }) as Record<string, FilteredMarketsAndOutcomeRelation[]>;

    filteredMarkets
        .forEach((market: FilteredMarkets) => {
            if (!mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id]) {
                mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id] = [];
            }
        });

    const marketsWithOutcomesAndBettingOffers: Record<string, ExtendedFilteredMarkets> =
        keyBy(setOutcomesAndBoToMarket(
            filteredMarkets,
            filteredOutcomes,
            mutableFilteredMarketAndOutcomeRelationOutcomeId,
            filteredBettingOffers,
        ), 'id');

    const filteredMatchList: FilteredMatchesWithEvents[] = setEventsToMatches(filteredMatches, filteredEventInfos);
    const filteredMatchesIds: string[] = filteredMatchList
        .map((filteredMatch: FilteredMatchesWithEvents): string => filteredMatch.id);

    const filteredMatchesWithEvents: Record<string, FilteredMatchesWithEvents> = keyBy(filteredMatchList, 'id');

    const discipline: AggregatorDisciplineModel | undefined = filteredMatchList.length
        ? records.find((item: AggregatorOneOfTypeModel): item is AggregatorDisciplineModel => {
            return item._type === ModelTypes.SPORT
                && item.id === filteredMatchList[0].sportId;
        })
        : undefined;

    return {
        discipline,
        filteredEventInfos,
        filteredMainMarketsList,
        filteredMainMarkets,
        filteredMarketGroupsOverviewList,
        filteredMarketGroupsOverview,
        filteredMarketAndOutcomeRelation,
        filteredOutcomes,
        filteredBettingOffers,
        filteredMatchesWithEvents,
        filteredMatchesIds,
        marketsWithOutcomesAndBettingOffers,
        filteredStreamingProviderEventRelation,
        filteredEventStatistics,
        filteredMainOutrightsMarketsList,
    };
}

export function filteredMatchesForRedirect(records: AggregatorOneOfTypeModel[]): FilteredMatches {
    const filteredMatches = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MATCH],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const filteredEventInfos = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_INFO],
        data: records,
    }) as Record<string, FilteredEventInfos>;

    const filteredMatchList: FilteredMatches[] = setEventsToMatches(filteredMatches, filteredEventInfos);

    return filteredMatchList[0];
}

export function filterItemsFromRecords(records: AggregatorOneOfTypeModel[], special?: boolean): FilterItemsFromRecordsReturn {
    const filteredMatches = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MATCH],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const filteredTournament = filterAndPick({
        filterPredicate: ['_type', ModelTypes.TOURNAMENT],
        filterByAllowLiveOdds: true,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const filteredEventInfos = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_INFO],
        data: records,
    }) as Record<string, FilteredEventInfos>;

    const filteredMainMarketsList = filterAndPick({
        filterPredicate: {'_type': special ? ModelTypes.MARKET : ModelTypes.MAIN_MARKET, 'outright': false},
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
        filterByMainMarket: true,
        special: special ? true : false,
    }) as string[];

    const filteredMainOutrightsMarketsList = filterAndPick({
        filterPredicate: {'_type': special ? ModelTypes.MARKET : ModelTypes.MAIN_MARKET, 'outright': true},
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
        filterByMainMarket: true,
        special: special ? true : false,
    }) as string[];

    const filteredMainMarkets = filterAndPick({
        filterPredicate: ['_type', special ? ModelTypes.MARKET : ModelTypes.MAIN_MARKET],
        data: records,
        filterByMainMarket: true,
        special: special ? true : false,
    }) as Record<string, FilteredMainMarkets>;

    const filteredMarketGroupsOverviewList = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    let filteredMarketGroupsOverview = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
    }) as Record<string, FilteredMarketGroupsOverview>;

    const showOutrights: boolean = getConfig().matchListConfig?.liveSports?.showOutrights ?? false;
    const filteredMatchEvent = showOutrights ? [...filteredMatches, ...filteredTournament] : filteredMatches;

    const filteredMatchList: FilteredMatchesWithEvents[] = setEventsToMatches(filteredMatchEvent, filteredEventInfos);
    const filteredMatchesIds: string[] = filteredMatchList
        .map((filteredMatch: FilteredMatchesWithEvents): string => filteredMatch.id);

    const filteredMatchesWithEvents: Record<string, FilteredMatchesWithEvents> = keyBy(filteredMatchList, 'id');

    const discipline: AggregatorDisciplineModel | undefined = filteredMatchList.length
        ? records.find((item: AggregatorOneOfTypeModel): item is AggregatorDisciplineModel => {
            return item._type === ModelTypes.SPORT
                && item.id === filteredMatchList[0].sportId;
        })
        : undefined;

    const filteredMarkets = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET],
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
    }) as FilteredMarkets[];

    const filteredBettingOffers = filterAndPick({
        filterPredicate: ['_type', ModelTypes.BETTING_OFFER],
        data: records,
    }) as Record<string, FilteredBettingOffers>;

    const filteredOutcomes = filterAndPick({
        filterPredicate: ['_type', ModelTypes.OUTCOME],
        data: records,
    }) as Record<string, FilteredOutcomes>;

    const filteredMarketAndOutcomeRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
        data: records,
    }) as Record<string, FilteredMarketsAndOutcomeRelation>;

    const filteredStreamingProviderEventRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.STREAMING_PROVIDER_EVENT_RELATION],
        data: records,
    }) as Record<string, FilteredStreamingProvider>;

    const filteredEventStatistics = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_STATISTICS],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredEventStatistics[];

    const mutableFilteredMarketAndOutcomeRelationOutcomeId =
        filterAndPick({
            filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
            data: records,
            groupDataBy: 'marketId',
        }) as Record<string, FilteredMarketsAndOutcomeRelation[]>;

    filteredMarkets
        .forEach((market: FilteredMarkets) => {
            if (!mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id]) {
                mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id] = [];
            }
        });

    const marketsWithOutcomesAndBettingOffers: Record<string, ExtendedFilteredMarkets> =
        keyBy(setOutcomesAndBoToMarket(
            filteredMarkets,
            filteredOutcomes,
            mutableFilteredMarketAndOutcomeRelationOutcomeId,
            filteredBettingOffers,
            filteredTournament,
        ), 'id');

    return {
        discipline,
        filteredEventInfos,
        filteredMainMarketsList,
        filteredMainMarkets,
        filteredMarketGroupsOverviewList,
        filteredMarketGroupsOverview,
        filteredMarketAndOutcomeRelation,
        filteredOutcomes,
        filteredBettingOffers,
        filteredMatchesWithEvents,
        filteredMatchesIds,
        marketsWithOutcomesAndBettingOffers,
        filteredStreamingProviderEventRelation,
        filteredEventStatistics,
        filteredMainOutrightsMarketsList,
    };
}

export function filterItemsSelectorFromRecords(records: AggregatorOneOfTypeModel[]): FilterItemsFromRecordsReturn {
    const filteredMatches = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MATCH],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const filteredTournament = filterAndPick({
        filterPredicate: ['_type', ModelTypes.TOURNAMENT],
        filterByAllowLiveOdds: true,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredMatches[];

    const filteredEventInfos = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_INFO],
        data: records,
    }) as Record<string, FilteredEventInfos>;

    let filteredMainMarketsList = filterAndPick({
        filterPredicate: {'_type': ModelTypes.MAIN_MARKET, 'outright': false},
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    let filteredMainOutrightsMarketsList = filterAndPick({
        filterPredicate: {'_type': ModelTypes.MAIN_MARKET, 'outright': true},
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    let filteredMainMarkets = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MAIN_MARKET],
        data: records,
    }) as Record<string, FilteredMainMarkets>;

    let filteredMarketGroupsOverviewList = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfIds,
    }) as string[];

    let filteredMarketGroupsOverview = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_GROUP_OVERVIEW],
        data: records,
    }) as Record<string, FilteredMarketGroupsOverview>;

    const showOutrights: boolean = getConfig().matchListConfig?.liveSports?.showOutrights ?? false;
    const filteredMatchEvent = showOutrights ?  [...filteredMatches, ...filteredTournament] : filteredMatches;

    const filteredMatchList: FilteredMatchesWithEvents[] = setEventsToMatches(filteredMatchEvent, filteredEventInfos);
    const filteredMatchesIds: string[] = filteredMatchList
        .map((filteredMatch: FilteredMatchesWithEvents): string => filteredMatch.id);

    const filteredMatchesWithEvents: Record<string, FilteredMatchesWithEvents> = keyBy(filteredMatchList, 'id');

    const discipline: AggregatorDisciplineModel | undefined = filteredMatchList.length
        ? records.find((item: AggregatorOneOfTypeModel): item is AggregatorDisciplineModel => {
            return item._type === ModelTypes.SPORT
                && item.id === filteredMatchList[0].sportId;
        })
        : undefined;

    const filteredMarkets = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET],
        data: records,
        returnType: FilterAndPickReturnType.ArrayOfObjects,
    }) as FilteredMarkets[];

    const filteredBettingOffers = filterAndPick({
        filterPredicate: ['_type', ModelTypes.BETTING_OFFER],
        data: records,
    }) as Record<string, FilteredBettingOffers>;

    const filteredOutcomes = filterAndPick({
        filterPredicate: ['_type', ModelTypes.OUTCOME],
        data: records,
    }) as Record<string, FilteredOutcomes>;

    const filteredMarketAndOutcomeRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
        data: records,
    }) as Record<string, FilteredMarketsAndOutcomeRelation>;

    const filteredStreamingProviderEventRelation = filterAndPick({
        filterPredicate: ['_type', ModelTypes.STREAMING_PROVIDER_EVENT_RELATION],
        data: records,
    }) as Record<string, FilteredStreamingProvider>;

    const filteredEventStatistics = filterAndPick({
        filterPredicate: ['_type', ModelTypes.EVENT_STATISTICS],
        returnType: FilterAndPickReturnType.ArrayOfObjects,
        data: records,
    }) as FilteredEventStatistics[];

    const mutableFilteredMarketAndOutcomeRelationOutcomeId =
        filterAndPick({
            filterPredicate: ['_type', ModelTypes.MARKET_OUTCOME_RELATION],
            data: records,
            groupDataBy: 'marketId',
        }) as Record<string, FilteredMarketsAndOutcomeRelation[]>;

    filteredMarkets
        .forEach((market: FilteredMarkets) => {
            if (!mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id]) {
                mutableFilteredMarketAndOutcomeRelationOutcomeId[market.id] = [];
            }
        });

    const marketsWithOutcomesAndBettingOffers: Record<string, ExtendedFilteredMarkets> =
        keyBy(setOutcomesAndBoToMarket(
            filteredMarkets,
            filteredOutcomes,
            mutableFilteredMarketAndOutcomeRelationOutcomeId,
            filteredBettingOffers,
            filteredTournament,
        ), 'id');

    const marketGroupsOverviewWithMarket: Record<string,MarketGroupsOverviewWithMarket> =
        keyBy(setMaketGroupToMainMarket(
            filteredMarkets,
            filteredMarketGroupsOverview,
            filteredMarketGroupsOverviewList,
        ),'id');
    
    const hasMarketSelector = true;
    filteredMainMarketsList = !hasMarketSelector ? filteredMainMarketsList : filteredMarketGroupsOverviewList;
    filteredMainMarkets = !hasMarketSelector ? filteredMainMarkets : marketGroupsOverviewWithMarket;

    return {
        discipline,
        filteredEventInfos,
        filteredMainMarketsList,
        filteredMainMarkets,
        filteredMainOutrightsMarketsList,
        filteredMarketGroupsOverviewList,
        filteredMarketGroupsOverview,
        filteredMarketAndOutcomeRelation,
        filteredOutcomes,
        filteredBettingOffers,
        filteredMatchesWithEvents,
        filteredMatchesIds,
        marketsWithOutcomesAndBettingOffers,
        filteredStreamingProviderEventRelation,
        filteredEventStatistics,
    };
}

export function setEventsToMatches(
    filteredMatches: FilteredMatches[],
    filteredEventInfos: Record<string, FilteredEventInfos>,
): FilteredMatchesWithEvents[] {

    return filteredMatches.map((match: FilteredMatches): FilteredMatchesWithEvents => {
        const { id } = match;
        const eventsRelatedToMatch = filter(filteredEventInfos, ['eventId', id]);

        const matchTimeElement: FilteredEventInfos | undefined = eventsRelatedToMatch.find((element: FilteredEventInfos) => element.eventId === id && element.typeId === EVENT_INFO.MATCH_TIME_ID);
        const remainingTimeElement: FilteredEventInfos | undefined = eventsRelatedToMatch.find((element: FilteredEventInfos) => element.eventId === id && element.typeId === EVENT_INFO.REMAINING_TIME_ID);
        const currentEventPartElement: FilteredEventInfos | undefined = eventsRelatedToMatch.find((element: FilteredEventInfos) => element.eventId === id && element.typeId === EVENT_INFO.CURRENT_EVENT_PART_ID);
        const scoreElements: FilteredEventInfos[] | undefined = filter(eventsRelatedToMatch, { 'eventId': id, 'typeId': EVENT_INFO.SCORE_ID });

        const redCardsElements = eventsRelatedToMatch.reduce<FilteredEventInfos | undefined>((result, element) => {
            if (element.eventId === id && element.typeId === EVENT_INFO.RED_CARDS) {
                if (element.eventPartId === '2') return element;
                if (!result && element.eventPartId === '3') return element;
            }

            return result;
        }, undefined);

        return {
            ...match,
            matchTimeId: matchTimeElement && matchTimeElement.id,
            remainingTimeId: remainingTimeElement && remainingTimeElement.id,
            currentEventPartId: currentEventPartElement && currentEventPartElement.id,
            scoreElements: orderBy(scoreElements, 'eventPartRelativePosition').map((el: FilteredEventInfos) => el.id),
            redCardsId: redCardsElements && redCardsElements.id,
        };
    });
}

export function setOutcomesAndBoToMarket(
    filteredMarkets: FilteredMarkets[],
    filteredOutcomes: Record<string, FilteredOutcomes>,
    filteredMarketAndOutcomeRelationOutcomeId: Record<string, FilteredMarketsAndOutcomeRelation[]>,
    filteredBettingOffers: Record<string, FilteredBettingOffers>,
    filteredTournament: FilteredMatches[] = [],
): ExtendedFilteredMarkets[] {
    return filteredMarkets.map<ExtendedFilteredMarkets>((market) => {
        let isOutright: boolean = false;

        if (filteredTournament) {
            const filterOutright: FilteredMatches[] = filteredTournament.filter((value) => value.id === market.eventId && value.allowsLiveOdds);
            isOutright = filterOutright.length ? true : false;
        }

        const marketAndOutcomeRelation: FilteredMarketsAndOutcomeRelation[] = filteredMarketAndOutcomeRelationOutcomeId[market.id] || [];
        const outcomes: FilteredOutcomes[] = marketAndOutcomeRelation.map((morElem: FilteredMarketsAndOutcomeRelation) => filteredOutcomes[morElem.outcomeId]);
        const outcomeList: string[] = isOutright
            ? getSortedOutcomesOdds(outcomes, filteredBettingOffers).map((el: FilteredOutcomes) => el.id)
            : getSortedOutcomes(outcomes, market.bettingTypeId).map((el: FilteredOutcomes) => el.id);

        const bettingOfferList: Array<string | undefined> = outcomeList.map((outcome: string) => {
            const localFilteredBettingOffer: FilteredBettingOffers[] = filter(
                filteredBettingOffers,
                (el: FilteredBettingOffers) => el.outcomeId === outcome && el.bettingTypeId === market.bettingTypeId,
            );
            const firstBettingOffer: FilteredBettingOffers | undefined = localFilteredBettingOffer[0];

            return firstBettingOffer && firstBettingOffer.id;
        });

        return {
            ...market,
            outcomeList,
            bettingOfferList,
        };
    });
}

export function getSortedOutcomesOdds(outcomes: FilteredOutcomes[], filteredBettingOffers: Record<string, FilteredBettingOffers>): FilteredOutcomes[] {
    return sortBy(outcomes, (el: FilteredOutcomes) => {
        const localFilteredBettingOffer: FilteredBettingOffers[] = filter(
            filteredBettingOffers,
            (elBettingOffer: FilteredBettingOffers) => elBettingOffer.outcomeId === el.id,
        );
        const firstBettingOffer: FilteredBettingOffers | undefined = localFilteredBettingOffer[0] ? localFilteredBettingOffer[0] : undefined;
        
        return firstBettingOffer?.odds || 0;
    });
}

export function getSortedOutcomes(outcomes: FilteredOutcomes[], betTypeId: string): FilteredOutcomes[] {
    return sortBy(outcomes, (el: FilteredOutcomes) => {
        if (sortByParamFloat1(betTypeId)) {
            return el.paramFloat1 || 0;
        }

        const sortByOrder: Record<string, number | undefined> | null = sortByMarketType(betTypeId);

        if (!sortByOrder) {
            return el;
        }

        return sortByOrder[el.headerNameKey];
    });
}

export function setMaketGroupToMainMarket(
    filteredMarkets: FilteredMarkets[],
    filteredMarketGroupsOverview: Record<string, FilteredMarketGroupsOverview>,
    filteredMarketGroupsOverviewList: string[],
): MarketGroupsOverviewWithMarket[] {

    return filteredMarketGroupsOverviewList.map((market: string): MarketGroupsOverviewWithMarket => {
        const filterGroupId: FilteredMarkets[] = filteredMarkets.filter((res) => res.groupIds?.indexOf(market) !== -1);
        const numberOfOutcomes: number = maxBy(filterGroupId, 'numberOfOutcomes')?.numberOfOutcomes || 3;
        const marketData: FilteredMarkets[] = filterGroupId.length ? Object.values(groupBy(filterGroupId, 'bettingTypeId')).sort((a, b) => b.length - a.length)[0] : [];

        const eventPartName: string = marketData.length ? marketData[0]?.eventPartName : '';
        const bettingTypeId: string = marketData.length ? marketData[0]?.bettingTypeId : '';
        const bettingTypeName: string = marketData.length ? marketData[0]?.bettingTypeName : '';
        const eventPartId: string = marketData.length ? marketData[0]?.eventPartId : '';

        return {
            ...filteredMarketGroupsOverview[market],
            bettingTypeId,
            bettingTypeName,
            eventPartId,
            eventPartName,
            numberOfOutcomes,
            hasSelector: true,
        };
    });
}